import React from "react";
import styled from "styled-components";

// Check if the avatar URL has a browser-displayable image format
const isDisplayableImage = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  const unsupportedExtensions = ['.dng', '.raw', '.cr2', '.nef', '.arw', '.orf', '.rw2'];
  return !unsupportedExtensions.some(ext => lowerUrl.endsWith(ext));
};

// Get consistent color based on user ID or name
const getAvatarColor = (identifier) => {
  const colors = [
    "#3B82F6", // blue
    "#EF4444", // red
    "#10B981", // green
    "#F59E0B", // amber
    "#8B5CF6", // purple
    "#EC4899", // pink
    "#06B6D4", // cyan
    "#84CC16", // lime
    "#F97316", // orange
    "#6366F1", // indigo
  ];

  if (typeof identifier === "number") {
    return colors[identifier % colors.length];
  }

  // For strings, use a simple hash
  if (typeof identifier === "string" && identifier.length > 0) {
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  return colors[0];
};

// Get initials from name
const getInitials = (firstName, lastName, fallbackName) => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (fallbackName) {
    const parts = fallbackName.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return fallbackName.charAt(0).toUpperCase();
  }
  return "?";
};

const AvatarContainer = styled.div`
  width: ${props => props.$size || "40px"};
  height: ${props => props.$size || "40px"};
  min-width: ${props => props.$size || "40px"};
  min-height: ${props => props.$size || "40px"};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${props => props.$bgColor || "#e2e8f0"};
  color: ${props => props.$textColor || "#ffffff"};
  font-weight: 600;
  font-size: ${props => props.$fontSize || "0.875rem"};
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Avatar = ({
  src,
  alt,
  firstName,
  lastName,
  name,
  userId,
  size = "40px",
  fontSize,
  style,
  className,
}) => {
  const canDisplayImage = isDisplayableImage(src);
  const initials = getInitials(firstName, lastName, name || alt);
  const bgColor = getAvatarColor(userId || name || firstName || "default");

  // Calculate font size based on avatar size if not provided
  const calculatedFontSize = fontSize || `calc(${size} * 0.4)`;

  return (
    <AvatarContainer
      $size={size}
      $bgColor={canDisplayImage ? "transparent" : bgColor}
      $textColor="#ffffff"
      $fontSize={calculatedFontSize}
      style={style}
      className={className}
    >
      {canDisplayImage ? (
        <img
          src={src}
          alt={alt || name || "User avatar"}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      ) : null}
      <span
        style={{
          display: canDisplayImage ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {initials}
      </span>
    </AvatarContainer>
  );
};

export default Avatar;
export { isDisplayableImage, getAvatarColor, getInitials };
